import { useState } from "react";
import { signChallengeWithEcp, ecpErrorMessage } from "../lib/ncalayer";
import { ecpChallengeApi } from "../api";
import { normalizeApiError } from "../lib/apiError";

/**
 * Кнопка «Войти / привязать / зарегистрировать через ЭЦП НУЦ РК».
 *
 * Props:
 *  - onSigned(challenge_id, cms_b64, identity?) — вызывается после успешной подписи.
 *      Каллер сам решает, что делать дальше (login / bind / register-company).
 *  - label — текст кнопки (по умолчанию «Войти через ЭЦП НУЦ РК»)
 *  - icon — компонент иконки (по умолчанию замочек)
 *  - variant — bootstrap-стиль ('outline-primary' default)
 *  - disabled — внешний disabled
 *  - className — доп. классы
 *  - testId
 */
export default function EcpButton({
  onSigned,
  label = "Войти через ЭЦП НУЦ РК",
  variant = "outline-primary",
  disabled = false,
  className = "",
  testId = "ecp-button",
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const handleClick = async () => {
    setError("");
    setBusy(true);
    try {
      // 1. Get challenge from backend
      const { challenge, challenge_id } = await ecpChallengeApi();
      // 2. Sign via NCALayer
      const cms_b64 = await signChallengeWithEcp(challenge);
      // 3. Pass to caller
      await onSigned(challenge_id, cms_b64);
    } catch (e) {
      const msg = normalizeApiError(e, ecpErrorMessage(e));
      setError(msg);
      // Полные технические детали — только в консоли F12 для разработчика.
      try { console.error("[ECP error]", e); } catch (_err) { /* ignore */ }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={`d-grid gap-2 ${className}`} data-testid={`${testId}-wrapper`}>
      <button
        type="button"
        onClick={handleClick}
        disabled={busy || disabled}
        className={`btn btn-${variant} d-flex align-items-center justify-content-center gap-2`}
        data-testid={testId}
        style={{ fontWeight: 500 }}
      >
        {busy ? (
          <>
            <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
            <span>Подписание…</span>
          </>
        ) : (
          <>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
              strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
              <path d="M7 11V7a5 5 0 0 1 10 0v4" />
            </svg>
            <span>{label}</span>
          </>
        )}
      </button>
      {error && (
        <div className="alert alert-danger py-2 px-3 small mt-1 mb-0" data-testid={`${testId}-error`}>
          <div className="d-flex align-items-start gap-2">
            <i className="bi bi-exclamation-triangle-fill flex-shrink-0" style={{ marginTop: "2px" }}></i>
            <div className="flex-grow-1">
              <div className="fw-semibold">{error}</div>
              {error.includes("NCALayer") && error.includes("не запущен") && (
                <div className="mt-1">
                  <a
                    href="https://pki.gov.kz/ncalayer-2/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="small"
                    data-testid={`${testId}-download-link`}
                  >
                    Скачать NCALayer →
                  </a>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
